import type { IDepositIncome, IDepositIncomeRate } from '~/components/entity/deposit/item/types'

interface ITermRange {
  min: number
  max: number
}

// Same simple-interest formula as the deposit calculator on the detail page,
// so both screens show one figure for one deposit.
const calcIncome = (amount: number, rate: number, months: number) =>
  (amount * rate * months) / (100 * 12)

// A tariff without its own lower bound covers its single term only.
const lowerBound = (rate: IDepositIncomeRate) => rate.period_from || rate.period

// Whether any term the tariff covers falls inside the range.
const isRateInRange = (rate: IDepositIncomeRate, range: ITermRange) =>
  rate.period >= range.min && lowerBound(rate) <= range.max

const toIncome = (
  rate: IDepositIncomeRate,
  amount: number,
  months: number,
  isOptimalTerm: boolean
): IDepositIncome => {
  const nominal = Number(rate.rate) || 0
  const income = calcIncome(amount, nominal, months)

  return {
    rate: nominal,
    effectiveRate: Number(rate.effective_rate) || 0,
    months,
    income,
    total: amount + income,
    currencyId: rate.currency_id,
    isOptimalTerm
  }
}

/**
 * Tariffs of one currency that accept the amount. Tariffs whose term range is
 * inverted are a data error and would otherwise match any term.
 */
const getApplicableRates = <T extends IDepositIncomeRate>(
  rates: T[],
  currencyId: number,
  amount: number
): T[] =>
  rates.filter(
    (rate) =>
      rate.currency_id === currencyId &&
      rate.amount_from <= amount &&
      (!rate.amount_to || amount <= rate.amount_to) &&
      lowerBound(rate) <= rate.period
  )

/**
 * Income for a term the user fixed. Unlike `findBestRate` in the deposit
 * calculator, the term has to fall inside the tariff range on both ends:
 * a deposit with no tariff for that term gets no income at all.
 */
const findIncomeForPeriod = (
  rates: IDepositIncomeRate[],
  amount: number,
  period: number
): IDepositIncome | null => {
  const covering = rates.filter((rate) => lowerBound(rate) <= period && period <= rate.period)
  if (!covering.length) return null

  const best = covering.reduce((top, rate) => (Number(rate.rate) > Number(top.rate) ? rate : top))

  return toIncome(best, amount, period, false)
}

/**
 * Income for the most profitable term. Within one tariff the rate is constant,
 * so its longest term earns the most; the tariffs are then compared with each
 * other. On a tie the shorter term wins — the same money sooner.
 *
 * Simple interest always favours the longest term, so without `range` a
 * multi-year deposit beats everything. The range keeps the search to terms a
 * user would actually choose: tariffs outside it are skipped, and a tariff
 * reaching past its upper end counts up to that end only.
 */
const findBestIncome = (
  rates: IDepositIncomeRate[],
  amount: number,
  range?: ITermRange
): IDepositIncome | null => {
  let best: IDepositIncome | null = null

  for (const rate of rates) {
    if (range && !isRateInRange(rate, range)) continue

    const months = range ? Math.min(rate.period, range.max) : rate.period
    const candidate = toIncome(rate, amount, months, true)

    const isBetter =
      !best ||
      candidate.income > best.income ||
      (candidate.income === best.income && candidate.months < best.months)

    if (isBetter) best = candidate
  }

  return best
}

export function useDepositIncome() {
  return { calcIncome, getApplicableRates, isRateInRange, findIncomeForPeriod, findBestIncome }
}
